import { Button, ScrollArea, Search, Switch } from '@ibsys/kit';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useHasPermission } from '@/entities/profile';
import { useFormGuardActions } from '@/entities/formGuard';
import { UserCard } from './ui/UserCard';
import { UsersList } from './ui/UsersList';
import { MasterDetailLayout } from '@/shared/ui';

export const UsersPage: FC = () => {
  const { t } = useTranslation(['common', 'users']);
  const canEdit = useHasPermission('ADMIN_USER');
  const hasDevelopment = useHasPermission('DEVELOPMENT');
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { setIsDirty } = useFormGuardActions();

  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);

  const handleCreate = () => {
    navigate('/users/lists/create');
  };

  const handleBack = () => {
    navigate('/users/lists');
  };

  const handleSelectUser = useCallback(
    (userId: number) => {
      if (String(userId) === id) {
        return;
      }
      navigate(`/users/lists/${userId}`);
    },
    [id, navigate],
  );

  return (
    <MasterDetailLayout
      isDetailOpen={Boolean(id)}
      onBack={handleBack}
      toolbar={
        <>
          <div className="flex items-center gap-4">
            {hasDevelopment && (
              <>
                <Search
                  className="flex-1"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('users:search')}
                />
                <Button variant="outline" size="md">
                  {t('common:search')}
                </Button>
              </>
            )}
            {canEdit && (
              <Button onClick={handleCreate} className="ml-auto">
                {t('users:addUser')}
              </Button>
            )}
          </div>
          <Switch value={showDeleted} onChange={setShowDeleted} label={t('common:showDeleted')} />
        </>
      }
      list={<UsersList showDeleted={showDeleted} onSelect={handleSelectUser} />}
      detail={
        <ScrollArea className="h-full">
          <UserCard userId={id} onUnsavedChange={(dirty) => setIsDirty(dirty)} />
        </ScrollArea>
      }
    />
  );
};
